import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Breadcrumbs from "@/components/Breadcrumbs";
import { Field, SelectField, TextareaField } from "@/pages/Contact";
import api from "@/lib/api";
import { toast } from "sonner";
import { CaretRight, CheckCircle } from "@phosphor-icons/react";
import { trackQuoteStarted, trackQuoteStep, pushGenerateLead } from "@/lib/analytics";
import { trackGoogleAdsConversion } from "@/lib/analytics_gads";
import { log } from "@/lib/logger";

const SITE_TYPES = [
  "Construction site",
  "Commercial property",
  "Auto dealership",
  "Equipment / storage yard",
  "Utility / energy",
  "Government / municipal",
  "Parking lot",
  "Vacant property",
  "Special event",
  "Critical infrastructure",
  "Other",
];

const RENTAL_DURATIONS = ["Short-term (< 1 month)", "1–3 months", "3–6 months", "6–12 months", "Long-term (12+ months)", "Not sure"];
const MONITORING = ["Self-monitoring", "Professional monitoring integration", "Not sure — recommend the best option"];
const SYSTEMS_OPTIONS = ["Not sure — recommend", "Mini Solar Pole System", "Standard 360° Trailer", "Pro Advanced 360° Trailer", "Multi-system"];

const STEPS = ["Contact", "Site", "System", "Details"];

export default function RequestQuote() {
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const nav = useNavigate();
  const [form, setForm] = useState({
    first_name: "", last_name: "", company: "", email: "", phone: "",
    city: "", state: "Nevada",
    site_type: SITE_TYPES[0], security_concern: "", deployment_date: "",
    rental_duration: RENTAL_DURATIONS[5], preferred_system: SYSTEMS_OPTIONS[0], monitoring_preference: MONITORING[2],
    site_size: "", num_units: "", referral_source: "", details: "",
    consent: true, website: "",
  });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  // Fire `quote_started` once on mount. `trackQuoteStarted` is a stable module-level import.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { trackQuoteStarted(); }, []);

  const canNext = () => {
    if (step === 0) return form.first_name && form.last_name && form.email && form.phone;
    if (step === 1) return form.city && form.state && form.site_type;
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post("/leads", form);
      // Fire GTM conversion event with hashed PII (Enhanced Conversions).
      try {
        await pushGenerateLead({
          email: form.email,
          phone: form.phone,
          company: form.company,
          service: form.preferred_system,
          industry: form.site_type,
          state: form.state,
          city: form.city,
          company_size: form.site_size,
          form_name: "Request a Quote",
          value: 1,
        });
        // Google Ads native conversion (gtag). No-ops if
        // REACT_APP_GOOGLE_ADS_CONVERSION_LABEL is not configured.
        trackGoogleAdsConversion({
          value: 1,
          currency: "USD",
          enhancedUserData: {
            email: form.email,
            phone_number: form.phone,
            address: {
              first_name: form.first_name,
              last_name: form.last_name,
            },
          },
        });
      } catch (analyticsErr) {
        log.error("analytics push failed:", analyticsErr);
      }
      toast.success("Request received");
      nav("/thank-you");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="quote-page">
      <section className="container-x pt-10 pb-6"><Breadcrumbs items={[{ label: "Request Assessment" }]} /></section>
      <section className="container-x pb-12">
        <div className="eyebrow text-solar mb-3">Site Assessment</div>
        <h1 className="mb-3 max-w-3xl">Tell us about your site. We'll take it from there.</h1>
        <p className="text-white/75 text-lg max-w-2xl">Four steps. About two minutes. A specialist follows up with a custom recommendation.</p>
      </section>

      <section className="container-x pb-24">
        <div className="grid lg:grid-cols-[280px_1fr] gap-10">
          {/* Progress rail */}
          <div className="lg:sticky lg:top-28 self-start">
            <ol className="space-y-1 border border-chrome bg-steel">
              {STEPS.map((s, i) => {
                const done = i < step;
                const active = i === step;
                let indicatorClass = "border border-chrome text-white/50";
                if (done) indicatorClass = "bg-solar text-black";
                else if (active) indicatorClass = "border border-solar text-solar";
                let labelClass = "text-white/60";
                if (active) labelClass = "text-solar";
                else if (done) labelClass = "text-white";
                return (
                  <li key={s} className={`flex items-center gap-3 px-4 py-3 border-b border-chrome last:border-b-0 ${active ? "bg-obsidian" : ""}`}>
                    <div className={`w-6 h-6 flex items-center justify-center text-xs font-bold ${indicatorClass}`}>
                      {done ? <CheckCircle size={16} weight="fill" /> : i + 1}
                    </div>
                    <span className={`text-sm uppercase tracking-wider font-semibold ${labelClass}`}>{s}</span>
                  </li>
                );
              })}
            </ol>
            <div className="mt-6 text-xs text-white/50 leading-relaxed">
              For urgent needs, call <a href="tel:7029290390" className="text-solar font-bold">702-929-0390</a>.
            </div>
          </div>

          {/* Step content */}
          <div className="border border-chrome bg-steel p-6 md:p-10">
            <input type="text" name="website" tabIndex="-1" autoComplete="off" style={{position:'absolute',left:'-9999px'}} value={form.website} onChange={set("website")} />

            {step === 0 && (
              <div className="space-y-4">
                <StepTitle title="Contact information" subtitle="How should we reach you?" />
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="First name" value={form.first_name} onChange={set("first_name")} testid="input-first-name" required />
                  <Field label="Last name" value={form.last_name} onChange={set("last_name")} testid="input-last-name" required />
                </div>
                <Field label="Company / Organization" value={form.company} onChange={set("company")} testid="input-company" />
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Business email" type="email" value={form.email} onChange={set("email")} testid="input-email" required />
                  <Field label="Phone" value={form.phone} onChange={set("phone")} testid="input-phone" required />
                </div>
              </div>
            )}

            {step === 1 && (
              <div className="space-y-4">
                <StepTitle title="Site details" subtitle="Where and what are we protecting?" />
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Project city" value={form.city} onChange={set("city")} testid="input-city" required />
                  <SelectField label="State" value={form.state} onChange={set("state")} testid="input-state" options={["Nevada", "Utah", "Other"]} />
                </div>
                <SelectField label="Site or project type" value={form.site_type} onChange={set("site_type")} testid="input-site-type" options={SITE_TYPES} />
                <TextareaField label="Main security concern" value={form.security_concern} onChange={set("security_concern")} testid="input-concern" rows={3} />
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Approximate site size" value={form.site_size} onChange={set("site_size")} testid="input-site-size" />
                  <Field label="Deployment date (target)" type="date" value={form.deployment_date} onChange={set("deployment_date")} testid="input-deployment-date" />
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-4">
                <StepTitle title="System preferences" subtitle="No wrong answer — we'll confirm during evaluation." />
                <SelectField label="Preferred system (if known)" value={form.preferred_system} onChange={set("preferred_system")} testid="input-preferred-system" options={SYSTEMS_OPTIONS} />
                <SelectField label="Rental duration" value={form.rental_duration} onChange={set("rental_duration")} testid="input-rental-duration" options={RENTAL_DURATIONS} />
                <SelectField label="Monitoring preference" value={form.monitoring_preference} onChange={set("monitoring_preference")} testid="input-monitoring" options={MONITORING} />
                <Field label="Number of units requested" value={form.num_units} onChange={set("num_units")} testid="input-num-units" />
              </div>
            )}

            {step === 3 && (
              <div className="space-y-4">
                <StepTitle title="Anything else?" subtitle="Extra details help us prepare a better recommendation." />
                <TextareaField label="Additional details" value={form.details} onChange={set("details")} testid="input-details" rows={6} />
                <Field label="How did you hear about us?" value={form.referral_source} onChange={set("referral_source")} testid="input-referral" />
                <label className="flex items-start gap-3 text-sm text-white/80 pt-3">
                  <input type="checkbox" data-testid="input-consent" checked={form.consent} onChange={(e)=>setForm({...form, consent:e.target.checked})} className="mt-1 w-4 h-4 accent-solar" />
                  <span>I consent to being contacted by 360 View LLC about my inquiry.</span>
                </label>
              </div>
            )}

            <div className="mt-8 pt-6 border-t border-chrome flex items-center justify-between">
              <button
                type="button"
                data-testid="quote-back-btn"
                onClick={() => setStep(Math.max(0, step - 1))}
                className={`btn-ghost ${step === 0 ? "opacity-40 pointer-events-none" : ""}`}
              >
                Back
              </button>
              {step < STEPS.length - 1 ? (
                <button
                  type="button"
                  data-testid="quote-next-btn"
                  disabled={!canNext()}
                  onClick={() => {
                    // Fire `quote_step_{n}` for the step the user is completing (1-based).
                    trackQuoteStep(step);
                    setStep(step + 1);
                  }}
                  className="btn-solar disabled:opacity-50"
                >
                  Continue <CaretRight size={12} weight="bold" className="inline ml-1" />
                </button>
              ) : (
                <button
                  type="button"
                  data-testid="quote-submit-btn"
                  disabled={submitting || !form.consent}
                  onClick={submit}
                  className="btn-solar disabled:opacity-50"
                >
                  {submitting ? "Submitting…" : "Submit Request"}
                </button>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function StepTitle({ title, subtitle }) {
  return (
    <div className="mb-6">
      <div className="font-display uppercase text-2xl font-bold mb-1">{title}</div>
      <div className="text-white/60 text-sm">{subtitle}</div>
    </div>
  );
}
